import gsap from "gsap";

const DEFAULT_WORD = "(Arkitekter)";
const HOLD_DURATION = 2;
const CHAR_DURATION = 0.06;

export default class Logo {
  private _word_element: HTMLElement;
  private _words: Array<string> = [];
  private _index: number = 0;
  private _timeline: gsap.core.Timeline;
  private _current_word: string = DEFAULT_WORD;

  constructor() {
    this._word_element = document.querySelector(".c-logo__word");

    if (!this._word_element) return;

    this._current_word = this._word_element.textContent || DEFAULT_WORD;
    window.addEventListener("newContentInserted", () => this.syncFromPage());
    this.syncFromPage();
  }

  private syncFromPage() {
    const words = this.readWords();
    if (this.sameWords(words)) return;

    this._words = words;
    this._index = 0;
    this.playNext();
  }

  private readWords(): Array<string> {
    const container: HTMLElement = document.querySelector(".c-main__inner");
    if (!container) return [];

    const raw = container.getAttribute("data-logo-words");
    if (!raw) return [];

    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];

      return parsed
        .map((word) => String(word).trim())
        .filter((word) => word.length);
    } catch (e) {
      return [];
    }
  }

  private sameWords(words: Array<string>): boolean {
    if (words.length !== this._words.length) return false;

    for (let i: number = 0; i < words.length; i++) {
      if (words[i] !== this._words[i]) return false;
    }

    return true;
  }

  private playNext() {
    this.killTimeline();
    this._current_word = this._word_element.textContent || "";

    if (!this._words.length) {
      if (this._current_word !== DEFAULT_WORD) {
        this.animateWord(this._current_word, DEFAULT_WORD, false);
      } else {
        this._word_element.textContent = DEFAULT_WORD;
      }
      return;
    }

    if (this._words.length === 1) {
      this.animateWord(this._current_word, this.wrap(this._words[0]), false);
      return;
    }

    this.animateWord(
      this._current_word,
      this.wrap(this._words[this._index]),
      true,
      () => {
        this._index = (this._index + 1) % this._words.length;
        this.playNext();
      },
    );
  }

  private animateWord(
    from: string,
    to: string,
    hold: boolean,
    onComplete?: () => void,
  ) {
    const timeline = gsap.timeline({
      onComplete: () => {
        this._current_word = to;
        if (onComplete) onComplete();
      },
    });

    this._timeline = timeline;

    if (from && from !== to) {
      const untype = { n: from.length };
      timeline.to(untype, {
        n: 0,
        duration: this.durationFor(from, true),
        ease: "none",
        onUpdate: () => {
          this._word_element.textContent = from.slice(0, Math.round(untype.n));
        },
      });
    }

    if (from !== to) {
      const type = { n: 0 };
      timeline.to(type, {
        n: to.length,
        duration: this.durationFor(to),
        ease: "none",
        onUpdate: () => {
          this._word_element.textContent = to.slice(0, Math.round(type.n));
        },
        onComplete: () => {
          this._word_element.textContent = to;
        },
      });
    }

    if (hold) timeline.to({}, { duration: HOLD_DURATION });
  }

  private wrap(word: string): string {
    return `(${word})`;
  }

  private durationFor(word: string, reverse: boolean = false): number {
    const per_char = reverse ? CHAR_DURATION / 3 : CHAR_DURATION;
    return Math.max(0.15, word.length * per_char);
  }

  private killTimeline() {
    if (this._timeline) {
      this._timeline.kill();
      this._timeline = null;
    }
  }
}
